// agtfitCoachAssistantService.js - ACTUALIZADO CON WEBSOCKET STREAMING REAL
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import WebSocketStreamingService from './webSocketStreamingService';

// ✅ Singleton lazy - solo se crea cuando se necesita
let webSocketServiceInstance = null;
const getWebSocketService = () => {
  if (!webSocketServiceInstance) {
    webSocketServiceInstance = new WebSocketStreamingService();
  }
  return webSocketServiceInstance;
};

// Configuración base usando variables de entorno
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5003'
  : (process.env.REACT_APP_API_FIT_URL || 'https://0nxh6fvws6.execute-api.us-east-2.amazonaws.com/dev');

// Prefijo de API según entorno
const API_PREFIX = process.env.NODE_ENV === 'development' ? '/api' : '';
const API_URL = `${API_BASE}${API_PREFIX}`;

// Crear cliente axios para peticiones normales (fallback)
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 60000
});

// Interceptor para añadir token de autenticación
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error al obtener token de Firebase:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Función para asegurar el tamaño de imagen
const ensureImageSize = async (imageBase64) => {
  if (imageBase64.length > 5 * 1024 * 1024) {
    console.warn('Imagen demasiado grande, reduciendo tamaño...');
    
    const img = new Image();
    
    await new Promise((resolve) => {
      img.onload = resolve;
      img.src = imageBase64;
    });
    
    let width = img.width;
    let height = img.height;
    
    const factor = Math.sqrt(5 * 1024 * 1024 / imageBase64.length);
    width = Math.floor(width * factor);
    height = Math.floor(height * factor);
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    
    return canvas.toDataURL('image/jpeg', 0.6);
  }
  
  return imageBase64;
};

// Fallback para simular streaming cuando WebSocket no está disponible
const fallbackStreamingQuery = async (query, conversationHistory, onToken, onComplete, onError) => {
  try {
    console.log('🔄 Usando fallback HTTP con simulación de streaming...');
    
    const response = await apiClient.post('/fit-assistant/query', {
      query,
      conversationHistory
    });
    
    if (response.data.success) {
      const message = response.data.message;
      
      // Simular streaming enviando caracteres/palabras gradualmente
      const words = message.split(' ');
      for (let i = 0; i < words.length; i++) {
        const word = words[i] + (i < words.length - 1 ? ' ' : '');
        
        // Enviar palabra completa
        for (const char of word) {
          onToken(char);
          // Pausa más realista entre caracteres
          await new Promise(resolve => setTimeout(resolve, 20));
        }
        
        // Pausa entre palabras
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      onComplete({ success: true, actions: response.data.actions || [] });
    } else {
      onError(new Error(response.data.message || 'Error en la consulta'));
    }
  } catch (error) {
    console.error('❌ Error en fallback:', error);
    onError(error);
  }
};

// Fallback para imágenes
const fallbackStreamingImageQuery = async (query, imageBase64, conversationHistory, onToken, onComplete, onError) => {
  try {
    console.log('🔄 Usando fallback HTTP imagen con simulación de streaming...');
    
    const processedImage = await ensureImageSize(imageBase64);
    
    const auth = getAuth();
    const user = auth.currentUser;
    let token = '';
    
    if (user) {
      token = await user.getIdToken();
    }
    
    const response = await fetch(`${API_URL}/fit-assistant/image-query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        query,
        imageBase64: processedImage,
        conversationHistory
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const textResponse = await response.text();
    
    let message = textResponse;
    let actions = [];
    
    if (textResponse.includes('__ACTIONS__:')) {
      const parts = textResponse.split('__ACTIONS__:');
      message = parts[0];
      try {
        actions = JSON.parse(parts[1]);
      } catch (error) {
        console.error('Error parsing actions:', error);
      }
    }
    
    // Simular streaming del mensaje
    const words = message.split(' ');
    for (let i = 0; i < words.length; i++) {
      const word = words[i] + (i < words.length - 1 ? ' ' : '');
      
      for (const char of word) {
        onToken(char);
        await new Promise(resolve => setTimeout(resolve, 25));
      }
      
      await new Promise(resolve => setTimeout(resolve, 60));
    }
    
    onComplete({ success: true, actions });
    
  } catch (error) {
    console.error('❌ Error en fallback imagen:', error);
    onError(error);
  }
};

// Servicio principal
const agtfitCoachAssistantService = {
  /**
   * 🚀 MÉTODO PRINCIPAL CON WEBSOCKET STREAMING REAL
   */
  sendQueryStream: async (query, conversationHistory = [], onToken, onComplete, onError) => {
    try {
      console.log('🚀 Iniciando consulta con WebSocket streaming real...');
      
      // Intentar primero con WebSocket para streaming real
      try {
        await getWebSocketService().sendQueryStream(
          query,
          conversationHistory,
          onToken,
          onComplete,
          onError
        );
        console.log('✅ WebSocket streaming exitoso');
      } catch (webSocketError) {
        console.warn('⚠️ WebSocket falló, usando fallback HTTP:', webSocketError.message);
        
        // Fallback a simulación de streaming HTTP
        await fallbackStreamingQuery(
          query,
          conversationHistory,
          onToken,
          onComplete,
          onError
        );
      }
    } catch (error) {
      console.error('❌ Error general en sendQueryStream:', error);
      onError(error);
    }
  },

  /**
   * 🖼️ MÉTODO DE IMAGEN CON WEBSOCKET STREAMING REAL
   */
  sendImageQueryStream: async (query, imageBase64, conversationHistory = [], onToken, onComplete, onError) => {
    try {
      console.log('🚀🖼️ Iniciando consulta imagen con WebSocket streaming real...');
      
      const processedImage = await ensureImageSize(imageBase64);
      
      // Intentar primero con WebSocket para streaming real
      try {
        await getWebSocketService().sendImageQueryStream(
          query,
          processedImage,
          conversationHistory,
          onToken,
          onComplete,
          onError
        );
        console.log('✅ WebSocket imagen streaming exitoso');
      } catch (webSocketError) {
        console.warn('⚠️ WebSocket imagen falló, usando fallback HTTP:', webSocketError.message);
        
        // Fallback a simulación de streaming HTTP
        await fallbackStreamingImageQuery(
          query,
          processedImage,
          conversationHistory,
          onToken,
          onComplete,
          onError
        );
      }
    } catch (error) {
      console.error('❌ Error general en sendImageQueryStream:', error);
      onError(error);
    }
  },

  /**
   * 🔌 MÉTODOS DE CONEXIÓN WEBSOCKET
   */
  connectWebSocket: async () => {
    try {
      await getWebSocketService().connect();
      console.log('✅ WebSocket conectado manualmente');
      return true;
    } catch (error) {
      console.error('❌ Error conectando WebSocket manualmente:', error);
      return false;
    }
  },

  disconnectWebSocket: () => {
    getWebSocketService().disconnect();
    console.log('🔌 WebSocket desconectado manualmente');
  },

  isWebSocketConnected: () => {
    return getWebSocketService().isConnected();
  },

  /**
   * 📊 MÉTODO PARA VERIFICAR ESTADO DE CONEXIÓN
   */
  getConnectionStatus: () => {
    return {
      webSocket: getWebSocketService().isConnected(),
      http: true // HTTP siempre está disponible como fallback
    };
  },

  // Métodos legacy para compatibilidad (sin streaming)
  sendQuery: async (query, conversationHistory = []) => {
    try {
      const response = await apiClient.post('/fit-assistant/query', { 
        query,
        conversationHistory
      });
      return response.data;
    } catch (error) {
      console.error('Error al enviar consulta al asistente de fitness:', error);
      return { 
        success: false, 
        message: 'Error al procesar tu consulta. Por favor intenta más tarde.',
        error: error.message || 'Error desconocido'
      };
    }
  },

  sendImageQuery: async (query, imageBase64, conversationHistory = []) => {
    try {
      const processedImage = await ensureImageSize(imageBase64);
      
      const auth = getAuth();
      const user = auth.currentUser;
      let token = '';
      
      if (user) {
        token = await user.getIdToken();
      }
      
      const response = await fetch(`${API_URL}/fit-assistant/image-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          query,
          imageBase64: processedImage,
          conversationHistory
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const textResponse = await response.text();
      
      let message = textResponse;
      let actions = [];
      
      if (textResponse.includes('__ACTIONS__:')) {
        const parts = textResponse.split('__ACTIONS__:');
        message = parts[0];
        try {
          actions = JSON.parse(parts[1]);
        } catch (error) {
          console.error('Error parsing actions:', error);
        }
      }
      
      return {
        success: true,
        message: message,
        actions: actions
      };
    } catch (error) {
      console.error('Error al enviar consulta con imagen al asistente de fitness:', error);
      return { 
        success: false, 
        message: 'Error al procesar tu imagen. Por favor intenta más tarde.',
        error: error.message || 'Error desconocido'
      };
    }
  }
};

// ✅ NO AUTO-CONECTAR - El WebSocket se conectará solo cuando se use AGTFit
// El servicio ahora usa lazy initialization y fallback automático a HTTP
// Si se necesita preconectar, debe hacerse desde el componente AGTFit específicamente

// Limpiar conexión al cerrar la página (solo si hay una conexión activa)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Solo desconectar si existe una instancia activa
    if (webSocketServiceInstance) {
      agtfitCoachAssistantService.disconnectWebSocket();
    }
  });
}

export default agtfitCoachAssistantService;